import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Landmark,
  Bell,
  Briefcase,
  Settings,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Zap,
  Sun,
  Moon,
  Search,
  Menu,
  X,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import { cn } from '@/lib/utils';
import { useCommandPaletteStore } from '@/store/commandPaletteStore';
import { GuidedOnboardingTour } from './GuidedOnboardingTour';
import { FeatureHints } from './FeatureHints';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
}

const mainNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Transactions', href: '/transactions', icon: FileText },
  { label: 'Insiders', href: '/insiders', icon: Users },
  { label: 'Companies', href: '/companies', icon: Building2 },
  { label: 'Investors', href: '/investors', icon: Landmark },
  { label: 'Alerts', href: '/alerts', icon: Bell },
];

const bottomNavItems: NavItem[] = [
  { label: 'FAQ', href: '/faq', icon: HelpCircle },
  { label: 'Settings', href: '/settings', icon: Settings },
];

// Private to staff accounts; the backend enforces the same limit.
const portfolioNavItem: NavItem = { label: 'My Portfolio', href: '/portfolio', icon: Briefcase };

/**
 * Layout Component
 * Theme-aware layout with sidebar navigation.
 */
export const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const visibleBottomNavItems = user?.is_staff
    ? [portfolioNavItem, ...bottomNavItems]
    : bottomNavItems;
  const { isDarkMode, toggleDarkMode } = useThemeStore();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileCloseButtonRef = useRef<HTMLButtonElement | null>(null);
  const openCommandPalette = useCommandPaletteStore((state) => state.open);

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileNavOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    if (!isMobileNavOpen) {
      return;
    }

    const menuButton = mobileMenuButtonRef.current;
    mobileCloseButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileNavOpen(false);
        menuButton?.focus();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const navigation = document.getElementById('mobile-navigation');
      const focusable = Array.from(
        navigation?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        ) ?? []
      );

      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      menuButton?.focus();
    };
  }, [isMobileNavOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMobileNavOpen(false);
  };

  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen bg-v2-bg flex overflow-x-hidden">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 hidden h-full border-r border-v2-border bg-v2-surface transition-all duration-300 z-40 lg:flex lg:flex-col',
          isCollapsed ? 'lg:w-16' : 'lg:w-64'
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-v2-border">
          {!isCollapsed && (
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-blue-600 to-cyan-400 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-v2-text-primary text-base">Avasar Signal Engine</span>
            </Link>
          )}
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-v2-text-muted hover:text-v2-text-primary hover:bg-v2-hover-bg transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {mainNavItems.map((item) => {
            const isActive = isActiveRoute(item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                data-tour={
                  item.href === '/transactions'
                    ? 'transactions-nav'
                    : item.href === '/alerts'
                      ? 'alerts-nav'
                      : item.href === '/dashboard'
                        ? 'dashboard-nav'
                        : undefined
                }
                data-hint={item.href === '/investors' ? 'investors-nav' : undefined}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
                  isActive
                    ? 'bg-v2-surface-raised text-v2-text-primary'
                    : 'text-v2-text-tertiary hover:text-v2-text-primary hover:bg-v2-hover-bg'
                )}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                {!isCollapsed && item.badge !== undefined && item.badge > 0 && (
                  <span className="ml-auto px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 text-[10px] font-bold border border-blue-500/20">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Navigation */}
        <div className="py-4 px-2 border-t border-v2-border">
          {visibleBottomNavItems.map((item) => {
            const isActive = isActiveRoute(item.href);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
                  isActive
                    ? 'bg-v2-surface-raised text-v2-text-primary'
                    : 'text-v2-text-tertiary hover:text-v2-text-primary hover:bg-v2-hover-bg'
                )}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            );
          })}

          {/* User Profile */}
          {!isCollapsed && user && (
            <div className="mt-4 pt-4 border-t border-v2-border">
              <div className="flex items-center gap-3 px-3">
                <div className="w-8 h-8 rounded-full bg-v2-surface-raised flex items-center justify-center border border-v2-border-hover text-xs font-bold text-v2-text-tertiary">
                  {user.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-v2-text-primary truncate">
                    {user.username}
                  </p>
                  <p className="text-[10px] text-v2-text-muted truncate">
                    {user.subscription?.tier_display || 'Free'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                type="button"
                className="mt-3 w-full flex items-center gap-3 px-3 py-2 text-v2-text-tertiary hover:text-v2-text-primary hover:bg-v2-hover-bg rounded-lg transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          )}

          {/* Collapsed User */}
          {isCollapsed && user && (
            <div className="mt-4 pt-4 border-t border-v2-border flex flex-col items-center">
              <div className="w-8 h-8 rounded-full bg-v2-surface-raised flex items-center justify-center border border-v2-border-hover text-xs font-bold text-v2-text-tertiary mb-2">
                {user.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <button
                onClick={handleLogout}
                type="button"
                className="p-2 text-v2-text-tertiary hover:text-v2-text-primary hover:bg-v2-hover-bg rounded-lg transition-all"
                title="Logout"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {isMobileNavOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm lg:hidden"
            onClick={() => setIsMobileNavOpen(false)}
            aria-label="Dismiss navigation menu"
          />

          <aside
            id="mobile-navigation"
            className="fixed inset-y-0 left-0 z-50 flex w-[88vw] max-w-sm flex-col border-r border-v2-border bg-v2-surface lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-navigation-title"
          >
            <div className="flex h-16 items-center justify-between border-b border-v2-border px-4">
              <Link
                to="/dashboard"
                className="flex items-center gap-2"
                onClick={() => setIsMobileNavOpen(false)}
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-tr from-blue-600 to-cyan-400">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p
                    id="mobile-navigation-title"
                    className="text-sm font-bold text-v2-text-primary"
                  >
                    Avasar Signal Engine
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-v2-text-dim">
                    Mobile Nav
                  </p>
                </div>
              </Link>
              <button
                ref={mobileCloseButtonRef}
                type="button"
                onClick={() => {
                  setIsMobileNavOpen(false);
                  mobileMenuButtonRef.current?.focus();
                }}
                className="flex h-11 w-11 items-center justify-center rounded-lg text-v2-text-muted transition-colors hover:bg-v2-hover-bg hover:text-v2-text-primary"
                aria-label="Close navigation menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="border-b border-v2-border px-4 py-4">
              <button
                type="button"
                onClick={() => {
                  openCommandPalette();
                  setIsMobileNavOpen(false);
                }}
                className="flex w-full items-center justify-between rounded-xl border border-v2-border-hover bg-v2-surface-raised px-3 py-3 text-left text-sm text-v2-text-secondary transition-colors hover:bg-v2-hover-bg hover:text-v2-text-primary"
              >
                <span className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-emerald-400" />
                  Search companies, insiders, filings
                </span>
                <span className="text-[10px] uppercase tracking-wider text-v2-text-dim">
                  Cmd/Ctrl+K
                </span>
              </button>
            </div>

            <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
              {mainNavItems.map((item) => {
                const isActive = isActiveRoute(item.href);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsMobileNavOpen(false)}
                    className={cn(
                      'flex items-center gap-3 rounded-xl px-3 py-3 transition-all',
                      isActive
                        ? 'bg-v2-surface-raised text-v2-text-primary'
                        : 'text-v2-text-tertiary hover:bg-v2-hover-bg hover:text-v2-text-primary'
                    )}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="space-y-4 border-t border-v2-border px-4 py-4">
              <div className="flex items-center justify-between rounded-xl border border-v2-border bg-v2-surface-raised px-3 py-3">
                <div>
                  <p className="text-sm font-medium text-v2-text-primary">Theme</p>
                  <p className="text-xs text-v2-text-muted">
                    {isDarkMode ? 'Dark mode enabled' : 'Light mode enabled'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleDarkMode}
                  className="flex h-11 w-11 items-center justify-center rounded-lg border border-v2-border-hover bg-v2-bg text-v2-text-primary transition-colors hover:bg-v2-hover-bg"
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>
              </div>

              <div className="space-y-1">
                {visibleBottomNavItems.map((item) => {
                  const isActive = isActiveRoute(item.href);
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsMobileNavOpen(false)}
                      className={cn(
                        'flex items-center gap-3 rounded-xl px-3 py-3 transition-all',
                        isActive
                          ? 'bg-v2-surface-raised text-v2-text-primary'
                          : 'text-v2-text-tertiary hover:bg-v2-hover-bg hover:text-v2-text-primary'
                      )}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </div>

              {user && (
                <div className="rounded-xl border border-v2-border bg-v2-surface-raised p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-v2-border-hover bg-v2-bg text-sm font-bold text-v2-text-tertiary">
                      {user.username?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-v2-text-primary">
                        {user.username}
                      </p>
                      <p className="truncate text-xs text-v2-text-muted">
                        {user.subscription?.tier_display || 'Free'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-v2-border-hover px-3 py-2 text-sm font-medium text-v2-text-secondary transition-colors hover:bg-v2-hover-bg hover:text-v2-text-primary"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </aside>
        </>
      )}

      {/* Main Content */}
      <main
        className={cn(
          'min-w-0 flex-1 transition-all duration-300',
          isCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        )}
      >
        {/* Top Bar */}
        <header className="h-16 bg-v2-bg/80 backdrop-blur-sm border-b border-v2-border sticky top-0 z-30">
          <div className="h-full w-full max-w-[1600px] min-w-0 mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <button
                ref={mobileMenuButtonRef}
                type="button"
                onClick={() => setIsMobileNavOpen(true)}
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-v2-border-hover bg-v2-surface-raised text-v2-text-primary transition-colors hover:bg-v2-hover-bg lg:hidden"
                aria-label="Open navigation menu"
                aria-controls="mobile-navigation"
                aria-expanded={isMobileNavOpen}
              >
                <Menu className="h-5 w-5" />
              </button>
              <Link
                to="/dashboard"
                aria-label="Avasar Signal Engine dashboard"
                className="flex items-center gap-2 lg:hidden"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-tr from-blue-600 to-cyan-400">
                  <Zap className="h-4 w-4 text-white" />
                </div>
                <span className="hidden text-sm font-bold text-v2-text-primary sm:inline">
                  Avasar Signal Engine
                </span>
              </Link>
              <div className="hidden shrink-0 items-center gap-2 lg:flex">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                <span className="text-xs text-v2-text-muted uppercase font-bold tracking-widest">
                  Live Data
                </span>
              </div>
            </div>
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              {/* Search Trigger */}
              <button
                type="button"
                onClick={openCommandPalette}
                data-tour="global-search"
                className="flex min-h-11 min-w-0 items-center gap-2 rounded-lg border border-v2-border-hover bg-v2-surface-raised px-2.5 py-2 text-v2-text-tertiary transition-colors hover:bg-v2-hover-bg hover:text-v2-text-primary sm:px-3 sm:py-1.5"
                title="Open Search (Cmd+K)"
                aria-label="Open search"
              >
                <Search className="w-4 h-4 text-emerald-400" />
                <span className="hidden text-sm sm:inline">Search...</span>
                <kbd className="hidden md:inline px-1.5 py-0.5 text-[10px] bg-v2-bg rounded border border-v2-border text-v2-text-dim">
                  {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+K
                </kbd>
              </button>
              {/* Theme Toggle */}
              <button
                type="button"
                onClick={toggleDarkMode}
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-v2-border-hover bg-v2-surface-raised text-v2-text-primary hover:bg-v2-hover-bg transition-colors"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <span className="hidden xl:inline text-[10px] text-v2-text-dim uppercase tracking-wider whitespace-nowrap">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="w-full max-w-[1600px] min-w-0 mx-auto px-4 py-6 sm:px-6">
          <Outlet />
        </div>
      </main>
      <GuidedOnboardingTour />
      <FeatureHints />
    </div>
  );
};

export default Layout;
